import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, Check, Upload, Loader2, CheckSquare, Square } from "lucide-react";
import { api, formatApiError, resolveMediaUrl } from "@/lib/api";
import FileUpload from "@/components/admin/FileUpload";

const fileTitle = (name = "") =>
  name.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ").trim() || "Foto galeri";

const AdminGallery = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [form, setForm] = useState({ title: "", album: "Umum", image: "", order: 0 });
  const [bulkAlbum, setBulkAlbum] = useState("Umum");
  const [progress, setProgress] = useState(null);
  const [selected, setSelected] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const bulkRef = useRef(null);

  const load = () => api.get("/gallery").then(({ data }) => {
    setItems(data);
    setSelected([]);
  });
  useEffect(() => { load(); }, []);

  const add = async () => {
    try {
      await api.post("/gallery", { ...form, order: Number(form.order || 0) });
      toast.success("Berhasil ditambahkan"); setOpen(false); setForm({ title: "", album: "Umum", image: "", order: 0 }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleOne = (id) => {
    setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  };

  const allSelected = items.length > 0 && selected.length === items.length;
  const toggleAll = () => setSelected(allSelected ? [] : items.map((g) => g.id));

  const bulkDelete = async (ids) => {
    if (!ids.length) return;
    if (!window.confirm(`Hapus ${ids.length} foto terpilih?`)) return;
    setDeleting(true);
    try {
      await api.post("/gallery/bulk-delete", { ids });
      toast.success(`${ids.length} foto dihapus`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal menghapus");
    } finally {
      setDeleting(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus?")) return;
    await api.delete(`/gallery/${id}`);
    load();
  };

  const uploadOne = async (file, order) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post(`/media/upload`, fd, {
      params: { folder: "gallery" },
      headers: { "Content-Type": "multipart/form-data" },
    });
    await api.post("/gallery", {
      title: fileTitle(file.name),
      album: bulkAlbum.trim() || "Umum",
      image: resolveMediaUrl(data.url),
      order,
    });
  };

  const handleBulkFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f && f.type.startsWith("image/"));
    if (!files.length) {
      toast.error("Pilih file gambar");
      return;
    }
    const oversized = files.filter((f) => f.size > 8 * 1024 * 1024);
    if (oversized.length) toast.error(`${oversized.length} file melebihi 8 MB dan dilewati`);
    const queue = files.filter((f) => f.size <= 8 * 1024 * 1024);
    if (!queue.length) return;

    const startOrder = items.reduce((max, g) => Math.max(max, Number(g.order || 0)), 0) + 1;
    setProgress({ done: 0, total: queue.length });
    let ok = 0;
    let failed = 0;

    for (let i = 0; i < queue.length; i += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await uploadOne(queue[i], startOrder + i);
        ok += 1;
      } catch (e) {
        failed += 1;
        console.error(e);
      }
      setProgress({ done: i + 1, total: queue.length });
    }

    setProgress(null);
    if (bulkRef.current) bulkRef.current.value = "";
    if (ok) {
      toast.success(`${ok} foto berhasil diunggah`);
      setBulkOpen(false);
      load();
    }
    if (failed) toast.error(`${failed} file gagal diunggah`);
  };

  const busy = !!progress;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-8 flex-wrap">
        <div>
          <h1 className="text-3xl font-display font-bold">Galeri</h1>
          <p className="mt-1 text-sm text-zinc-500">Kelola foto & album. Bisa upload dan hapus banyak foto sekaligus.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {items.length > 0 && (
            <>
              <button
                type="button"
                onClick={toggleAll}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border font-semibold text-sm"
              >
                {allSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                {allSelected ? "Batal pilih" : "Pilih semua"}
              </button>
              <button
                type="button"
                onClick={() => bulkDelete(selected)}
                disabled={!selected.length || deleting}
                data-testid="bulk-delete-gallery"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-red-200 text-red-600 font-semibold text-sm hover:bg-red-50 disabled:opacity-40"
              >
                {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                Hapus terpilih{selected.length ? ` (${selected.length})` : ""}
              </button>
            </>
          )}
          <button
            onClick={() => setBulkOpen(true)}
            data-testid="bulk-gallery"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-maroon-800 text-maroon-800 font-semibold hover:bg-maroon-50"
          >
            <Upload size={16} /> Upload massal
          </button>
          <button onClick={() => setOpen(true)} data-testid="new-gallery" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-maroon-800 text-white font-semibold">
            <Plus size={16} /> Tambah Foto
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((g) => (
          <div key={g.id} className={`group relative aspect-square rounded-2xl overflow-hidden bg-zinc-100 ${selected.includes(g.id) ? "ring-2 ring-maroon-700" : ""}`}>
            <img src={resolveMediaUrl(g.image)} alt={g.title} className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => toggleOne(g.id)}
              className={`absolute top-3 left-3 w-9 h-9 rounded-full flex items-center justify-center shadow ${selected.includes(g.id) ? "bg-maroon-800 text-white" : "bg-white/95 text-zinc-700"}`}
              aria-label="Pilih"
            >
              {selected.includes(g.id) ? <CheckSquare size={16} /> : <Square size={16} />}
            </button>
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition p-4 flex flex-col justify-end pointer-events-none">
              <div className="text-white text-sm font-semibold">{g.title}</div>
              <div className="text-white/70 text-xs">{g.album}</div>
            </div>
            <button onClick={() => del(g.id)} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white text-red-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full p-10 text-center text-zinc-500">Belum ada foto.</div>}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-display font-bold">Tambah Foto</h3>
              <button onClick={() => setOpen(false)} className="p-2 rounded-lg hover:bg-zinc-100"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <Input label="Judul *" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
              <Input label="Album" value={form.album} onChange={(v) => setForm({ ...form, album: v })} />
              <FileUpload label="Foto *" value={form.image} onChange={(v) => setForm({ ...form, image: v })} folder="gallery" aspect="square" testid="gf-image-upload" />
              <Input label="Urutan" value={form.order} onChange={(v) => setForm({ ...form, order: v })} type="number" />
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setOpen(false)} className="px-5 py-2.5 rounded-full border font-semibold">Batal</button>
              <button onClick={add} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-maroon-800 text-white font-semibold"><Check size={16} /> Simpan</button>
            </div>
          </div>
        </div>
      )}

      {bulkOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-display font-bold">Upload massal</h3>
              <button onClick={() => !busy && setBulkOpen(false)} className="p-2 rounded-lg hover:bg-zinc-100" disabled={busy}><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <Input label="Album" value={bulkAlbum} onChange={setBulkAlbum} />
              <p className="text-xs text-zinc-500">Pilih banyak foto sekaligus. Judul diambil dari nama file. Max 8 MB per file.</p>
              <input
                ref={bulkRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => handleBulkFiles(e.target.files)}
              />
              {busy ? (
                <div>
                  <div className="flex items-center gap-2 text-sm font-semibold text-maroon-800 mb-2">
                    <Loader2 size={16} className="animate-spin" /> Mengunggah {progress.done}/{progress.total}
                  </div>
                  <div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
                    <div className="h-full bg-maroon-700 transition-all" style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }} />
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => bulkRef.current?.click()}
                  className="w-full rounded-2xl border-2 border-dashed border-zinc-200 bg-zinc-50 py-12 flex flex-col items-center gap-2 text-zinc-500 hover:border-maroon-400 hover:text-maroon-700"
                >
                  <Upload size={22} />
                  <span className="text-sm font-semibold">Klik untuk pilih banyak foto</span>
                  <span className="text-xs text-zinc-400">PNG, JPG, WEBP · bisa select semua</span>
                </button>
              )}
            </div>
            <div className="mt-6 flex justify-end">
              <button onClick={() => setBulkOpen(false)} disabled={busy} className="px-5 py-2.5 rounded-full border font-semibold disabled:opacity-50">Tutup</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Input = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <label className="block text-sm font-semibold text-zinc-800 mb-2">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full px-4 py-3 rounded-xl border border-zinc-200 focus:border-maroon-700 outline-none" />
  </div>
);

export default AdminGallery;
